import { type ReactNode } from 'react';

import {
  COLOR_TOKENS,
  type ColorToken,
  DonutChart,
  type DonutSegment,
  LegendRow,
  LineChart,
  type LineChartSeries,
  PageHeader,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { LubavButton } from '@backrs_ui/grow-ui/src/components/Lubav/LubavButton';
import { type EngagementResponse } from '@backrs_ui/grow-ui/src/redux/coachingAnalytics';

import { Box, type SxProps, type Theme, Tooltip, Typography } from '@mui/material';

import { humanize, percentOf, percentShares } from './analytics.labels';
import { ENGAGEMENT_TOP } from './analytics.layouts';
import { mutedTextSx, pieLegendSx, pieRowSx } from './analytics.styles';
import { AnalyticsCard } from './AnalyticsCard';
import { AnalyticsCardGrid } from './AnalyticsCardGrid';
import { useAgentContext, useTopCardsAgentContext } from './EngagementCards.agentContext';

const { engagement } = appStrings.modules.coachingAnalytics;

type RecencyBucketId = keyof EngagementResponse['recently_active']['buckets'];

const RECENCY_BUCKETS: readonly { id: RecencyBucketId; label: string; color: ColorToken }[] = [
  { id: 'last_30', label: engagement.recency.last30, color: 'Blue/800' },
  { id: 'days_31_60', label: engagement.recency.days31to60, color: 'Blue/500' },
  { id: 'days_61_90', label: engagement.recency.days61to90, color: 'Blue/300' },
  { id: 'over_90', label: engagement.recency.over90, color: 'Blue/100' },
];

type ActivityKey = 'posts' | 'goals' | 'comments';

const ACTIVITY_SERIES: readonly { key: ActivityKey; label: string; color: ColorToken }[] = [
  { key: 'posts', label: engagement.activity.studentPosts, color: 'Brand/800' },
  { key: 'goals', label: engagement.activity.studentGoals, color: 'Brand/500' },
  { key: 'comments', label: engagement.activity.studentComments, color: 'Brand/300' },
];

const NETWORK_ROW_LIMIT = 8;
const NETWORK_TYPE_COLORS: readonly ColorToken[] = [
  'Brand/900',
  'Brand/700',
  'Brand/500',
  'Brand/300',
  'Brand/100',
];
const PIE_SIZE = 150;

export const VISIBLE_MONTHS = 6;

const monthLabel = (value: string): string => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
};

type TopCardProps = Readonly<{ data: EngagementResponse; onClick?: () => void }>;

const LastActiveCard = ({ data, onClick }: TopCardProps) => {
  const { buckets, total } = data.recently_active;
  const shares = percentShares(
    RECENCY_BUCKETS.map((bucket) => buckets[bucket.id].count),
    total,
  );
  const rows = RECENCY_BUCKETS.map((bucket, index) => ({
    ...bucket,
    pct: shares[index],
  }));
  const segments: DonutSegment[] = rows.map((row) => ({
    label: row.label,
    value: row.pct,
    color: row.color,
  }));

  return (
    <AnalyticsCard
      title={engagement.recency.title}
      subtitle={engagement.recency.subtitle}
      info={[engagement.recency.info]}
      id="coaching-analytics-card-recently-active"
      onClick={onClick}
    >
      <Box sx={pieRowSx}>
        <DonutChart variant={{ type: 'multi', segments }} size={PIE_SIZE} cutout={0} />
        <Box sx={pieLegendSx}>
          {rows.map((row) => (
            <LegendRow key={row.id} color={row.color} label={row.label} pct={row.pct} />
          ))}
        </Box>
      </Box>
    </AnalyticsCard>
  );
};

const ActivityCard = ({ data, onClick }: TopCardProps) => {
  const months = data.activity.monthly.series.slice(-VISIBLE_MONTHS);
  const series: readonly LineChartSeries[] = ACTIVITY_SERIES.map((line) => ({
    id: line.key,
    points: months.map((month) => month[line.key]),
    color: line.color,
  }));

  return (
    <AnalyticsCard
      title={engagement.activity.title}
      subtitle={engagement.activity.subtitle(months.length)}
      info={[engagement.activity.info]}
      id="coaching-analytics-card-activity"
      onClick={onClick}
      belowHeader={
        <Box sx={activityLegendSx}>
          {ACTIVITY_SERIES.map((line) => (
            <LegendRow key={line.key} color={line.color} label={line.label} />
          ))}
        </Box>
      }
    >
      <Box sx={activityChartWrapSx}>
        <LineChart series={series} ariaLabel={engagement.activity.title} showValues />
        <Box sx={activityHoverRowSx}>
          {months.map((month, index) => (
            <Tooltip
              key={month.index}
              arrow
              placement="top"
              title={engagement.activity.month(
                monthLabel(month.start),
                month.posts,
                month.goals,
                month.comments,
              )}
            >
              <Box sx={activityHoverCellSx(index === 0 || index === months.length - 1)} />
            </Tooltip>
          ))}
        </Box>
      </Box>
      <Box sx={weekAxisSx}>
        {months.map((month) => (
          <Typography key={month.index} variant="text-xs-medium" sx={weekLabelSx}>
            {monthLabel(month.start)}
          </Typography>
        ))}
      </Box>
    </AnalyticsCard>
  );
};

const NetworkMembershipCard = ({ data }: Readonly<{ data: EngagementResponse }>) => {
  const rows = data.networks.slice(0, NETWORK_ROW_LIMIT);
  const max = Math.max(...rows.map((row) => row.count), 0);

  const typeLabel = (type: string | null): string =>
    type ? humanize(type) : engagement.networkMembership.otherType;

  const types = rows
    .map((row) => typeLabel(row.type))
    .filter((type, index, all) => all.indexOf(type) === index);
  const colorOf = (type: string | null): ColorToken =>
    NETWORK_TYPE_COLORS[
      Math.min(Math.max(types.indexOf(typeLabel(type)), 0), NETWORK_TYPE_COLORS.length - 1)
    ];

  return (
    <AnalyticsCard
      title={engagement.networkMembership.title}
      subtitle={engagement.networkMembership.subtitle}
      info={[engagement.networkMembership.info(NETWORK_ROW_LIMIT)]}
      belowHeader={
        types.length > 0 ? (
          <Box sx={networkLegendSx}>
            {types.map((type) => (
              <LegendRow key={type} color={colorOf(type)} label={type} />
            ))}
          </Box>
        ) : null
      }
    >
      {rows.length === 0 ? (
        <Typography variant="text-sm-regular" sx={mutedTextSx}>
          {engagement.networkMembership.empty}
        </Typography>
      ) : (
        <Box sx={networkListSx}>
          {rows.map((row) => {
            const pct = percentOf(row.count, max);

            return (
              <Tooltip
                key={row.group_id}
                arrow
                placement="top"
                title={engagement.networkMembership.members(row.name, row.count)}
              >
                <Box sx={networkRowSx}>
                  <Typography variant="text-sm-medium" sx={networkLabelSx}>
                    {row.name}
                  </Typography>
                  <Box sx={networkTrackSx}>
                    <Box
                      sx={networkFillSx(pct > 0 && pct < 100)}
                      style={{
                        width: `${pct}%`,
                        backgroundColor: COLOR_TOKENS[colorOf(row.type)],
                      }}
                    />
                  </Box>
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      )}
    </AnalyticsCard>
  );
};

type EngagementSectionProps = Readonly<{
  data: EngagementResponse;
  /** Rendered Tool Usage card, placed below this section's own cards. */
  toolUsage: ReactNode;
}>;

/** The engagement cards keyed by the area names the layouts place them under. */
export const engagementCards = (
  data: EngagementResponse,
  onCardClick?: () => void,
): Record<string, ReactNode> => ({
  recency: <LastActiveCard data={data} onClick={onCardClick} />,
  activity: <ActivityCard data={data} onClick={onCardClick} />,
});

export const EngagementTopCards = ({
  data,
  onCardClick,
}: Readonly<{ data: EngagementResponse; onCardClick?: () => void }>) => {
  useTopCardsAgentContext(data, VISIBLE_MONTHS);

  return <AnalyticsCardGrid layout={ENGAGEMENT_TOP} cards={engagementCards(data, onCardClick)} />;
};

export const EngagementSection = ({ data, toolUsage }: EngagementSectionProps) => {
  useAgentContext(data, NETWORK_ROW_LIMIT);

  return (
    <Box sx={sectionSx}>
      <PageHeader
        title={engagement.title}
        subtitle={engagement.subtitle}
        maxWidth={null}
        rightComponents={[
          <LubavButton
            key="engagement-discuss-lubav"
            id="coaching-analytics-engagement-discuss-lubav"
            label={engagement.discussWithLubav}
            promptButtonId="coaching_analytics_engagement"
          />,
        ]}
      />
      <EngagementTopCards data={data} />
      <Box sx={bottomGridSx}>
        <NetworkMembershipCard data={data} />
        {toolUsage}
      </Box>
    </Box>
  );
};

const sectionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
  width: '100%',
});

const bottomGridSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: backrs.spacing['spacing-3xl'],
  alignItems: 'stretch',
  '@container (min-width: 900px)': {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  },
});

// Where LineChart's plot area starts and ends, as a share of its viewBox: the
// left clears the value-label gutter it adds under `showValues`. Anything
// aligned to a point needs the same insets.
const AXIS_INSET_LEFT = '7.8%';
const AXIS_INSET_RIGHT = '2.5%';

const activityChartWrapSx: SxProps<Theme> = {
  position: 'relative',
  width: '100%',
};

// Half-width end cells so each hover region centres on its month's point.
const activityHoverRowSx: SxProps<Theme> = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  paddingLeft: AXIS_INSET_LEFT,
  paddingRight: AXIS_INSET_RIGHT,
  boxSizing: 'border-box',
};

const activityHoverCellSx = (isEdge: boolean): SxProps<Theme> => ({
  flex: isEdge ? 0.5 : 1,
  minWidth: 0,
});

const activityLegendSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexWrap: 'nowrap',
  gap: backrs.spacing['spacing-xl'],
  width: '100%',
});

const weekAxisSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
  paddingLeft: AXIS_INSET_LEFT,
  paddingRight: AXIS_INSET_RIGHT,
  boxSizing: 'border-box',
};

const weekLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const networkLegendSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xl'],
  width: '100%',
});

const networkListSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  width: '100%',
});

const networkRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
  width: '100%',
});

/** Half-width card, so names wrap to a second line rather than clipping. */
const networkLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
  width: 152,
  flexShrink: 0,
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  wordBreak: 'break-word',
});

const networkTrackSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flex: 1,
  height: 28,
  borderRadius: backrs.radius['radius-md'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  backgroundColor: backrs.colors['Brand/50'],
  overflow: 'hidden',
});

const networkFillSx =
  (hasDivider: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    height: '100%',
    boxSizing: 'border-box',
    borderRight: hasDivider ? `1px solid ${backrs.colors['Colors/Base/Black']}` : 'none',
  });
